import { CopyOutlined, CloseOutlined, CaretUpOutlined, CaretDownOutlined } from "@ant-design/icons";
import { Button, Space, Tag, Typography, message, Divider } from "antd";
import { useState } from "react";

import { useTheme } from "src/contexts/ThemeContext";
import { copyTextToClipboard } from "src/utils/clipboardHelper";

const { Text } = Typography;

const TranscriptPreview = ({
  chunks = [],
  summary = "",
  onClear,
  loading = false,
  title = "Transcript Preview",
  initialExpanded = true
}) => {
  const { isDarkMode } = useTheme();
  const [isExpanded, setIsExpanded] = useState(initialExpanded);

  if (!loading && (!chunks || chunks.length === 0) && !summary) return null;

  return (
    <div
      style={{
        marginTop: 16,
        border: `1px solid ${isDarkMode ? "#303030" : "#e6f7ff"}`,
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: isDarkMode ? "#1f1f1f" : "#fafafa",
        width: "100%"
      }}
    >
      <div
        style={{
          padding: "8px 16px",
          backgroundColor: isDarkMode ? "#111b26" : "#e6f7ff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom:
            isExpanded || summary ? `1px solid ${isDarkMode ? "#303030" : "#bae7ff"}` : "none"
        }}
      >
        <Text strong style={{ color: isDarkMode ? "#177ddc" : "#0050b3" }}>
          {title} {chunks.length > 0 && `(${chunks.length} chunks)`}
        </Text>
        <Space>
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={async (e) => {
              e.preventDefault();
              const fullText =
                (summary ? `${summary}\n\n` : "") + chunks.map(({ text }) => text).join(" ");
              const hasCopied = await copyTextToClipboard(fullText);
              if (!hasCopied) {
                message.error("Failed to copy transcript");
                return;
              }
              message.success("Transcript and summary copied to clipboard!");
            }}
          >
            Copy
          </Button>
          {onClear && (
            <Button
              size="small"
              danger
              type="text"
              icon={<CloseOutlined />}
              onClick={(e) => {
                e.preventDefault();
                onClear();
              }}
            >
              Clear
            </Button>
          )}
        </Space>
      </div>
      <div
        style={{
          maxHeight: isExpanded ? "450px" : "auto",
          overflowY: isExpanded ? "auto" : "hidden",
          padding: "0 16px",
          backgroundColor: isDarkMode ? "#141414" : "#fff"
        }}
      >
        {loading ? (
          <div style={{ padding: "16px", textAlign: "center" }}>
            <Text type="secondary">Loading transcript preview...</Text>
          </div>
        ) : (
          <>
            {summary && (
              <div
                style={{
                  padding: "16px",
                  margin: "16px 0",
                  backgroundColor: isDarkMode
                    ? "rgba(23, 125, 220, 0.05)"
                    : "rgba(0, 80, 179, 0.05)",
                  borderRadius: "6px",
                  borderLeft: `4px solid ${isDarkMode ? "#177ddc" : "#1890ff"}`
                }}
              >
                <div style={{ marginBottom: 4 }}>
                  <Text
                    strong
                    style={{
                      fontSize: "12px",
                      color: isDarkMode ? "#177ddc" : "#1890ff",
                      textTransform: "uppercase"
                    }}
                  >
                    Content Summary
                  </Text>
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    lineHeight: "1.6",
                    color: isDarkMode ? "#d9d9d9" : "#262626",
                    whiteSpace: "pre-wrap"
                  }}
                >
                  {summary}
                </div>
              </div>
            )}

            {chunks.length > 0 && (
              <div style={{ paddingBottom: isExpanded ? 16 : 0 }}>
                {summary && isExpanded && <Divider style={{ margin: "8px 0 16px 0" }} />}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    paddingBottom: isExpanded ? 16 : 12,
                    paddingTop: !summary && !isExpanded ? 12 : 0
                  }}
                >
                  <Button
                    type="link"
                    size="small"
                    onClick={() => setIsExpanded(!isExpanded)}
                    style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}
                  >
                    {isExpanded ? (
                      <>
                        <CaretUpOutlined /> Hide Full Transcript
                      </>
                    ) : (
                      <>
                        <CaretDownOutlined /> Show Full Transcript
                      </>
                    )}
                  </Button>
                </div>

                {isExpanded &&
                  chunks.map((chunk, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: "16px 0",
                        borderBottom:
                          idx === chunks.length - 1
                            ? "none"
                            : `1px solid ${isDarkMode ? "#303030" : "#f0f0f0"}`
                      }}
                    >
                      <Tag
                        color={isDarkMode ? "blue" : "cyan"}
                        style={{
                          marginBottom: 8,
                          borderRadius: "4px",
                          fontWeight: "500",
                          fontSize: "12px"
                        }}
                      >
                        {chunk.start}s - {chunk.end}s
                      </Tag>
                      <div
                        style={{
                          fontSize: "14px",
                          lineHeight: "1.6",
                          color: isDarkMode ? "#d9d9d9" : "#262626"
                        }}
                      >
                        {chunk.text}
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {!summary && chunks.length === 0 && (
              <div style={{ padding: "16px", textAlign: "center" }}>
                <Text type="secondary">No transcript content available.</Text>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TranscriptPreview;
